/** React hooks built on top of MCPTTProvider's snapshot. */

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MCPTTCtx, type MCPTTApi } from "./MCPTTProvider";
import { PttControlType, type Call, type Device, type Group, type Network, type User } from "@mcptt/client";

export function useMCPTT(): MCPTTApi {
  const ctx = useContext(MCPTTCtx);
  if (!ctx) throw new Error("useMCPTT() called outside <MCPTTProvider>");
  return ctx;
}

/** Connection lifecycle state. Cheap re-render — flips during sign-in. */
export function useConnectionState() {
  return useMCPTT().snapshot.state;
}

/** All networks the dispatcher has access to. */
export function useNetworks(): ReadonlyArray<Network> {
  return useMCPTT().snapshot.networks;
}

/** Single network by ID, or undefined while still initializing. */
export function useNetwork(networkId: string | null | undefined): Network | undefined {
  const networks = useNetworks();
  return useMemo(
    () => (networkId ? networks.find((n) => n.ID === networkId) : undefined),
    [networks, networkId],
  );
}

/**
 * The lookup hooks below intentionally return a NEW array each time
 * the snapshot changes, even if the underlying `network.users/groups/
 * devices` array reference is identical.
 *
 * Why: the MCPTTClient mutates User / Device objects in place when
 * DATAEX deltas (incl. DeviceParameters pushes for selected-group /
 * battery) arrive. The provider then publishes a fresh snapshot
 * object, but `network.devices` is still the same Array reference.
 * That defeats every downstream `useMemo([devices])` — the stale
 * memoized values (e.g. SubscribersPanel's `rows`) keep their old
 * `selectedGroup` / `battery` reads even though the object underneath
 * has the new value.
 *
 * Spreading once at this layer breaks the reference chain at the
 * cheapest possible point. The cost is O(n) array copy on every
 * snapshot tick — negligible vs full immutable updates inside the
 * client, and invisible to consumers.
 */
export function useSubscribers(networkId: string | null | undefined): User[] {
  const { snapshot } = useMCPTT();
  return useMemo(() => {
    const net = networkId ? snapshot.networks.find((n) => n.ID === networkId) : undefined;
    return [...((net?.users ?? []) as User[])];
  }, [snapshot, networkId]);
}

export function useTalkgroups(networkId: string | null | undefined): Group[] {
  const { snapshot } = useMCPTT();
  return useMemo(() => {
    const net = networkId ? snapshot.networks.find((n) => n.ID === networkId) : undefined;
    return [...((net?.groups ?? []) as Group[])];
  }, [snapshot, networkId]);
}

export function useDevices(networkId: string | null | undefined): Device[] {
  const { snapshot } = useMCPTT();
  return useMemo(() => {
    const net = networkId ? snapshot.networks.find((n) => n.ID === networkId) : undefined;
    return [...((net?.devices ?? []) as Device[])];
  }, [snapshot, networkId]);
}

/** Live + recently-finished calls. Cheapest source for "what's transmitting". */
export function useCalls(): ReadonlyArray<Call> {
  return useMCPTT().snapshot.calls;
}

/** Active (un-resolved) emergency events, keyed by EmergencyID. */
export function useEmergencies() {
  return useMCPTT().snapshot.emergencies;
}

/**
 * Look up the logged-in user's display name by walking the network
 * roster. Returns null until the roster has at least one network
 * containing our userId. The dispatcher account typically appears as
 * a Type=1 user in EVERY network it has access to, so the first hit
 * wins.
 */
export function useLoggedInUser(): { id: string; login: string; name: string } | null {
  const { snapshot } = useMCPTT();
  return useMemo(() => {
    const id = snapshot.userId;
    if (!id) return null;
    for (const net of snapshot.networks) {
      const u = (net.users ?? []).find((x) => x.ID === id);
      if (u) return { id, login: u.Login ?? "", name: u.Name ?? u.Login ?? "" };
    }
    return { id, login: "", name: "" };
  }, [snapshot.userId, snapshot.networks]);
}

/**
 * Per-talkgroup activity rollup. Builds a map of GroupID → activity
 * record from the live `calls` array AND a sticky "last seen" log
 * the hook maintains in a ref so finished calls don't disappear from
 * the UI immediately when their `state` flips to "ended".
 *
 * Activity record:
 *   - state: "rx" while a radio/other dispatcher is talking,
 *            "tx" while this dispatcher is talking, "hangtime"
 *            right after release, "idle" otherwise
 *   - lastTalker: callsign / name of the most recent transmitter
 *   - lastCallId: CallID of the most recent call (live or recent)
 *   - sinceMs: ms since the call's PRESSED frame began (for the
 *              "↻ 2m" / "↻ 14s" timer)
 */
export interface TalkgroupActivity {
  state:        "rx" | "tx" | "hangtime" | "idle";
  lastTalker?:  string;
  lastCallId?:  string;
  /** Wall-clock ms when the last RELEASED frame arrived (or live press
   *  start if state==="tx"). */
  lastEventAt?: number;
}

export function useTalkgroupActivity(networkId: string | null | undefined): Map<string, TalkgroupActivity> {
  const { snapshot, client } = useMCPTT();
  const stickyRef = useRef<Map<string, TalkgroupActivity>>(new Map());
  const [version, force] = useState(0);

  // Subscribe to PTT events to update sticky state and trigger re-render.
  useEffect(() => {
    const off = client.on("ptt", (call: Call, control: PttControlType) => {
      if (call.NetworkID !== networkId) return;
      const isPressed = control === PttControlType.VoiceGroupPressed
                     || control === PttControlType.VoicePrivatePressed;
      const isReleased = control === PttControlType.VoiceGroupReleased
                      || control === PttControlType.VoicePrivateReleased;
      const isEnd = control === PttControlType.VoiceGroupEnd
                 || control === PttControlType.VoicePrivateEnd;
      const next: TalkgroupActivity = stickyRef.current.get(call.TargetID) ?? { state: "idle" };
      if (isPressed) {
        next.state       = call.SourceUserID === snapshot.userId ? "tx" : "rx";
        next.lastTalker  = call.SourceName ?? call.SourceID.slice(0, 6);
        next.lastCallId  = call.CallID;
        next.lastEventAt = Date.now();
      } else if (isReleased) {
        next.state       = "hangtime";
        next.lastEventAt = Date.now();
      } else if (isEnd) {
        next.state       = "hangtime";
        next.lastEventAt = Date.now();
        window.setTimeout(() => {
          const cur = stickyRef.current.get(call.TargetID);
          if (cur?.lastCallId === call.CallID && cur.state === "hangtime") {
            cur.state = "idle";
            stickyRef.current.set(call.TargetID, cur);
            force((n) => n + 1);
          }
        }, 5000);
      }
      stickyRef.current.set(call.TargetID, next);
      force((n) => n + 1);
    });
    return off;
  }, [client, networkId, snapshot.userId]);

  // Also fold in the current snapshot.calls so we catch any state we
  // missed before subscribing (e.g. after a network swap).
  return useMemo(() => {
    const m = new Map(stickyRef.current);
    for (const c of snapshot.calls) {
      if (c.NetworkID !== networkId) continue;
      const cur = m.get(c.TargetID);
      if (c.state === "speaking") {
        m.set(c.TargetID, {
          state:       c.SourceUserID === snapshot.userId ? "tx" : "rx",
          lastTalker:  c.SourceName ?? c.SourceID.slice(0, 6),
          lastCallId:  c.CallID,
          lastEventAt: cur?.lastEventAt ?? c.startedAt,
        });
      } else if (c.state === "hangtime" && (!cur || (cur.state !== "tx" && cur.state !== "rx"))) {
        m.set(c.TargetID, {
          state:       "hangtime",
          lastTalker:  c.SourceName ?? c.SourceID.slice(0, 6),
          lastCallId:  c.CallID,
          lastEventAt: cur?.lastEventAt ?? Date.now(),
        });
      }
    }
    return m;
  }, [snapshot.calls, snapshot.userId, networkId, version]);
}

/**
 * Tick every `intervalMs` and return the current Date.now(). Used by
 * UI elements that show "x seconds ago" — they read this value so
 * React re-renders them on every tick.
 */
export function useTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * GPS-ping ring buffer driven by the client's `device:gps` event.
 *
 * One entry per inbound GPS_RESULT_TO_CLIENT frame. Most recent first.
 * Capped to `max` (default 30) — adequate for an active ops floor;
 * older pings drop off but their effect on `device.Latitude/Longitude`
 * persists in the underlying network roster.
 *
 * `isFresh` flips false 8 seconds after arrival so the map can stop
 * pulsing the marker. The hook reuses a single setInterval to age
 * the freshness across all entries — cheaper than per-entry timers.
 */
export interface GpsPing {
  id:        string;     // synthetic — `${ts}-${seq}`
  ts:        number;     // wall-clock ms
  deviceId:  string;
  userId:    string;
  name:      string;     // looked up against the roster at arrival time
  lat:       number;
  lon:       number;
  accuracy:  number | undefined;
  isFresh:   boolean;
}

const PING_BUFFER_MAX  = 30;
const PING_FRESH_MS    = 8_000;
const PING_AGE_TICK_MS = 1_000;

export function useGpsPings(networkId: string | null | undefined): GpsPing[] {
  const { client, snapshot } = useMCPTT();
  const [pings, setPings] = useState<GpsPing[]>([]);
  const seqRef = useRef(0);

  // Resolve a user's display name from the current snapshot. Done in
  // a ref-y way so the listener doesn't need to be re-attached every
  // time the roster mutates.
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  useEffect(() => {
    if (!networkId) return;
    // eslint-disable-next-line no-console
    console.info("[mcptt] useGpsPings: subscribing for network", networkId);
    const off = client.on("device:gps", (device, evNetworkId) => {
      // eslint-disable-next-line no-console
      console.info("[mcptt] device:gps event", {
        evNetworkId, expected: networkId, deviceId: device.ID,
        lat: device.Latitude, lon: device.Longitude,
      });
      if (evNetworkId !== networkId) return;
      if (typeof device.Latitude !== "number" || typeof device.Longitude !== "number") return;
      // Look up the friendly name from the roster at arrival time.
      let name = device.UserName ?? device.Login ?? device.UserID.slice(0, 6);
      const net = snapshotRef.current.networks.find((n) => n.ID === networkId);
      const u   = (net?.users ?? []).find((x) => x.ID === device.UserID);
      if (u) name = u.Name ?? u.Login ?? name;

      const ping: GpsPing = {
        id:        `${Date.now()}-${++seqRef.current}`,
        ts:        Date.now(),
        deviceId:  device.ID,
        userId:    device.UserID,
        name,
        lat:       device.Latitude,
        lon:       device.Longitude,
        accuracy:  typeof device.Accuracy === "number" ? device.Accuracy : undefined,
        isFresh:   true,
      };
      setPings((prev) => {
        const next = [ping, ...prev];
        return next.length > PING_BUFFER_MAX ? next.slice(0, PING_BUFFER_MAX) : next;
      });
    });
    return () => {
      // eslint-disable-next-line no-console
      console.info("[mcptt] useGpsPings: unsubscribing for network", networkId);
      off();
    };
  }, [client, networkId]);

  // Age `isFresh` flags every second. Single interval shared across
  // all entries; no-op when nothing is fresh.
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      setPings((prev) => {
        let changed = false;
        const next = prev.map((p) => {
          if (p.isFresh && now - p.ts >= PING_FRESH_MS) {
            changed = true;
            return { ...p, isFresh: false };
          }
          return p;
        });
        return changed ? next : prev;
      });
    }, PING_AGE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Reset on network swap — IDs are network-scoped.
  useEffect(() => { setPings([]); }, [networkId]);

  return pings;
}
